import React, { useEffect, useState } from 'react';

import {
  Breakpoints,
  CTA,
  InputAppearanceValue,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  ThemeProvider,
} from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { applyTextTransformations } from '../../../../shared/utils/applyTextTransformations';
import { NoTranslateTextInput } from '../../../../shared/components/no-translate-text-input';
import {
  formatPhoneNumber,
  getPrimaryPhone,
} from '../../../../shared/utils/general';
import {
  StyledButtonContainer,
  StyledPhoneFormWrapper,
  StyledPhoneInfoContainer,
} from './styled';
import { UserData } from '../../../../shared/typings/main';
import {
  onVWBasicButtonClickWithProcessEvent,
  onVWBasicLayerLoadEvent,
} from '../../../../shared/tagging/tagging-helpers';

const US_MOBILE_NUMBER_REGEX = /^[0-9]{10}$/;
const PHONE_VALIDATION_SCHEMA = Yup.object({
  number: Yup.string()
    .matches(US_MOBILE_NUMBER_REGEX, {
      message: 'registration.personal.information.mobileNumber.error.invalid',
      excludeEmptyString: true,
    })
    .required('registration.personal.information.mobileNumber.error.empty'),
});

interface EditPhoneFormViewProps {
  authService: any;
  translationHandler: (key: string) => string;
  trackingManager: any;
  localeCode: string;
  updateUserProfile: (data: Partial<UserData>) => void;
  user: UserData;
  onSuccess: () => void;
  onChange: () => void;
  isCanada: boolean;
}

export const EditPhoneFormView = ({
  authService,
  translationHandler,
  trackingManager,
  localeCode,
  updateUserProfile,
  user,
  onChange,
  onSuccess,
  isCanada
}: EditPhoneFormViewProps) => {
  const [formattedNumber, setFormattedNumber] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const initialPhoneNumber = getPrimaryPhone(user);
  const initialFormatted = formatPhoneNumber(initialPhoneNumber).formatted;

  useEffect(() => {
    if (isCanada) {
      onVWBasicLayerLoadEvent(trackingManager, 'Edit Phone Number');
    }
  }, []);

  const updateUserPhones = async (number: string) => {
    const currentPhones = user.phones || [];
    const updatedPhones = currentPhones.map((phone) => {
      const { __typename, ...phoneWithoutTypename } = phone;
      if (phone.primary) return { ...phoneWithoutTypename, number };
      return phoneWithoutTypename;
    });
    try {
      user.phones = updatedPhones;
      await authService.updateProfileDynamicGqlRequest(user, localeCode);
      updateUserProfile({ phones: updatedPhones });
      onSuccess();
    } catch (error) {
      console.error('There was an error updating the user phones', error);
      authService.apiLog({ message: error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: { number: initialPhoneNumber.replace(/\D/g, '') },
    onSubmit: async ({ number }) => {
      setIsSubmitting(true);

      // Track button click for Update number CTA
      try {
        onVWBasicButtonClickWithProcessEvent(trackingManager, {
          Name: 'Account Settings',
          StepName: 'Personal Information and Security - Mobile Number',
          StepNumber: '4.3',
        }, { name: 'Update number' });
      } catch (error) {
        console.warn('Error tracking button click:', error);
      }
      await updateUserPhones(number);
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: PHONE_VALIDATION_SCHEMA,
  });

  const handlePhoneChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { formatted, raw } = formatPhoneNumber(e.currentTarget.value);

    setFormattedNumber(formatted);
    formik.setFieldValue('number', raw);
  };

  const isButtonDisabled =
    Object.keys(formik.errors).length > 0 ||
    !formik.dirty ||
    formik.isSubmitting ||
    isSubmitting;

  useEffect(() => {
    setFormattedNumber(initialFormatted);
  }, [initialFormatted]);

  useEffect(() => {
    if (formik.dirty && onChange) {
      onChange();
    }
  }, [formik.values, onChange, formik.dirty]);

  return (
    <ThemeProvider theme="main">
      <StyledPhoneFormWrapper>
        <form onSubmit={formik.handleSubmit}>
          <NoTranslateTextInput
            value={formattedNumber}
            onChange={handlePhoneChange}
            onBlur={formik.handleBlur('number')}
            name="number"
            message={
              formik.touched.number && formik.errors.number
                ? translationHandler(formik.errors.number)
                : undefined
            }
            appearance={
              formik.touched.number && formik.errors.number
                ? ('error' as InputAppearanceValue)
                : undefined
            }
            aria-label={translationHandler('registration.form.mobile.number.label')}
            aria-required="true"
            label={translationHandler('registration.form.mobile.number.label')}
            required
          />
          <StyledPhoneInfoContainer>
            <TextCustom
              appearance={TextAppearance.copy200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  color: TextColor.primary,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
              }}
            >
              {applyTextTransformations(translationHandler('registration.form.phone.information'), ['PIN', 'myVW'])}
            </TextCustom>
          </StyledPhoneInfoContainer>
          <StyledButtonContainer>
            <CTA
              aria-label="Save number CTA"
              disabled={isButtonDisabled}
              emphasis="primary"
              stretchContent
              tag="button"
              type="submit"
            >
              {translationHandler('accountSettings.edit.phone')}
            </CTA>
          </StyledButtonContainer>
        </form>
      </StyledPhoneFormWrapper>
    </ThemeProvider>
  );
};